import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "antd";
import {
  DeleteOutlined,
  LoadingOutlined,
  SearchOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { DEPOSIT_LOAD_PERMISSION, type Deposit } from "../../../../../api/deposits";
import { useAvailableDeposits } from "../../../../../hooks/useDeposits";
import { useIssueDepositReceiptAction } from "../../../../../hooks/useIssueDepositReceiptAction";
import { useViewport } from "../../../../../hooks/useViewport";
import { searchDeposits } from "../../../../../lib/depositSearch";
import { useAppSelector } from "../../../../../store/hooks";
import { getApiErrorMessage } from "../../../../../utils/translations";
import {
  DangerButton,
  GhostButton,
  ModalFooter,
  ModalHeader,
  posModalStyles as modalStyles,
} from "../../modalPrimitives";
import { useOrderingMessage } from "../../useOrderingMessage";
import { Center } from "../clients/ClientRow";
import { depositPickBlockOf } from "./billDeposit";
import { DepositPickerRow } from "./DepositPickerRow";

interface DepositPickerModalProps {
  open: boolean;
  /** Сметката, към която се закача — закаченият към нея е маркираният ред,
   *  закачените към други маси са неактивни. */
  billId: number;
  tableName: string;
  /** Закаченият в момента депозит; null — без депозит. */
  attachedId: number | null;
  onPick: (deposit: Deposit) => void;
  /** „Махни депозита" — само при закачен. */
  onRemove: () => void;
  /** Заявката (закачане/махане) тече — бутоните са заключени. */
  busy: boolean;
  onClose: () => void;
}

/**
 * „Използвай депозит" — изборът на депозит за отворената сметка (BE
 * `GET /deposits/available`): активните, първо тези с най-близка „За дата".
 * Един клик закача; на десктоп и стрелки + Enter, Esc затваря. Депозит без
 * касов бон е неактивен — „Издай бон" (с правото „Зареждане с депозит")
 * издава бона тук; закаченият към друга маса — неактивен с името ѝ.
 * Със закачен депозит той е маркиран, а „Махни депозита" го маха.
 */
export function DepositPickerModal({
  open,
  billId,
  tableName,
  attachedId,
  onPick,
  onRemove,
  busy,
  onClose,
}: DepositPickerModalProps) {
  const { t } = useTranslation();
  const message = useOrderingMessage();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const { data: deposits = [], isLoading, error } = useAvailableDeposits(open);
  const permissions = useAppSelector((state) => state.auth.permissions);
  const canIssueReceipt = (permissions ?? []).some(
    (permission) => permission.name === DEPOSIT_LOAD_PERMISSION,
  );
  const { issueReceipt, issuingDepositId, isConfirming } = useIssueDepositReceiptAction(message);
  const [searchText, setSearchText] = useState("");
  const [highlightedId, setHighlightedId] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setSearchText("");
      setHighlightedId(attachedId);
    }
  }, [open, attachedId]);

  // Име, описание, номерът на депозита или на бона му (съвпаденията по номер —
  // най-отгоре, за да ги вземе Enter).
  const shownDeposits = useMemo(
    () =>
      searchDeposits(deposits, searchText, (deposit) => [deposit.name, deposit.description]),
    [deposits, searchText],
  );
  const freeCount = deposits.filter((deposit) => deposit.status === "active").length;

  useEffect(() => {
    if (highlightedId == null) return;
    listRef.current
      ?.querySelector(`[data-deposit-id="${highlightedId}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [highlightedId]);

  useEffect(() => {
    // Въпросът на „Издай бон" отгоре слуша клавишите си сам.
    if (!open || !isDesktop || isConfirming) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        if (shownDeposits.length === 0) return;
        event.preventDefault();
        const index = shownDeposits.findIndex((deposit) => deposit.id === highlightedId);
        const nextIndex =
          event.key === "ArrowDown"
            ? Math.min(index + 1, shownDeposits.length - 1)
            : Math.max(index - 1, 0);
        setHighlightedId(shownDeposits[nextIndex].id);
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        if (busy) return;
        const deposit =
          shownDeposits.find((candidate) => candidate.id === highlightedId) ??
          shownDeposits[0];
        if (!deposit) return;
        // Без бон или на друга маса — BE отказва; казваме защо.
        const block = depositPickBlockOf(deposit, billId);
        if (block === null) onPick(deposit);
        else
          message.warning(
            block === "otherBill"
              ? t("depositAction.attachedElsewhere", { table: deposit.usedBillName ?? "" })
              : t("depositReceipt.notReadyHint"),
          );
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [
    open,
    isDesktop,
    isConfirming,
    shownDeposits,
    highlightedId,
    onPick,
    onClose,
    message,
    t,
    busy,
    billId,
  ]);

  const width = isMobile ? undefined : isTablet ? 520 : 600;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="flex max-h-[85vh] flex-col bg-pos-card text-pos-ink">
        <ModalHeader
          icon={<WalletOutlined />}
          title={t("depositAction.useTitle", { table: tableName })}
          subtitle={t("closeTable.deposit.pickerSubtitle", { count: freeCount })}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        <div
          className={`flex min-h-0 flex-1 flex-col gap-3 pb-3 pt-4 ${
            isNarrow ? "px-4" : "px-6"
          }`}
        >
          <label className="flex h-11 shrink-0 items-center gap-3 rounded-[10px] border border-pos-line3 bg-pos-card px-4">
            <SearchOutlined className="text-[17px] text-pos-dim" />
            <input
              value={searchText}
              autoFocus={isDesktop}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder={t("closeTable.deposit.searchPlaceholder")}
              className="min-w-0 flex-1 border-0 bg-transparent p-0 text-base font-semibold text-pos-ink outline-none placeholder:text-pos-muted md:text-sm"
            />
          </label>

          <div
            ref={listRef}
            className="min-h-[120px] max-h-[55vh] flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {isLoading ? (
              <Center>
                <LoadingOutlined spin className="text-[36px] text-pos-accent" />
              </Center>
            ) : error ? (
              <Center tone="danger">{getApiErrorMessage(error, t("common.error"))}</Center>
            ) : shownDeposits.length === 0 ? (
              <Center>
                {searchText
                  ? t("closeTable.deposit.noMatches")
                  : t("closeTable.deposit.noDeposits")}
              </Center>
            ) : (
              <div className="flex flex-col gap-1.5">
                {shownDeposits.map((deposit) => (
                  // Един светнат ред: маркираният със стрелките (в началото —
                  // закаченият).
                  <DepositPickerRow
                    key={deposit.id}
                    deposit={deposit}
                    billId={billId}
                    isHighlighted={deposit.id === (highlightedId ?? attachedId)}
                    isAttachedHere={deposit.id === attachedId}
                    onPick={busy ? () => {} : onPick}
                    onIssueReceipt={canIssueReceipt ? issueReceipt : null}
                    isIssuing={issuingDepositId === deposit.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <ModalFooter isNarrow={isNarrow}>
          <GhostButton
            label={t("common.cancel")}
            onClick={onClose}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          {attachedId !== null && (
            <DangerButton
              icon={<DeleteOutlined />}
              label={t("depositAction.remove")}
              onClick={onRemove}
              loading={busy}
            />
          )}
        </ModalFooter>
      </div>
    </Modal>
  );
}
